import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = [
    'wrapper', 'button', 'buttonLabel', 'dropdown',
    'search', 'clearSearchBtn', 'options'
  ]
  static values = {
    direction: { type: String, default: 'auto' },
    minWidth: Number,
    maxWidth: Number,
  };

  connect() {
    this.refreshBoundActions = this.refreshBoundActions.bind(this)
    document.addEventListener("turbo:frame-load", this.refreshBoundActions)

    const dd = this.element.querySelector('[data-ss-dropdown]');
    if (!dd) return;
    
    this.dropdownEl = dd.cloneNode(true);
    this.dropdownOriginParent = dd.parentElement;
    this.portalRoot = this._ensurePortalRoot();
    this.isDropdownOpen = false;
    
    // Cache the hidden field reference
    this._hiddenField = this.element.querySelector('input[type="hidden"]');
  }

  disconnect() {
    document.removeEventListener("turbo:frame-load", this.refreshBoundActions)
    this._teardownOutside();
    this._detachObservers();
    this._unbindDropdownHandlers();
    if (this.dropdownEl && this.dropdownEl.parentElement === this.portalRoot) {
      this.dropdownOriginParent.appendChild(this.dropdownEl);
    }
  }

  refreshBoundActions(event) {
    const frame = event.target
    if (this.element.contains(frame)) {
      this.application.load()
    }
  }

  toggle(e) {
    e?.preventDefault();
    e?.stopPropagation();
    if (!this.dropdownEl) return;
    this.isDropdownOpen ? this.close() : this.open();
  }

  open() {
    if (!this.dropdownEl || this.isDropdownOpen) return;

    // teleport
    this.dropdownEl.innerHTML = this.dropdownTarget.innerHTML
    this.portalRoot.appendChild(this.dropdownEl);

    this.dropdownEl.classList.remove('hidden');
    this.dropdownEl.classList.add('invisible', 'ss-transition-none', 'opacity-0', 'scale-95');

    // a11y
    this.buttonTarget.setAttribute('aria-expanded', 'true');

    // position
    this._positionDropdown();

    // bind after teleport
    this._bindDropdownHandlers();
    this._grabCommonRefs();

    // reset search + hide "No results"
    if (this._emptyEl) this._emptyEl.hidden = true;
    if (this._searchEl) {
      this._searchEl.value = '';
      this._toggleClearBtn(false);
      this._filter('');
      try {
        this._searchEl.focus({ preventScroll: true });
      } catch (_) {}
    }

    const currentSelected = this._getHiddenValue();
    this._updateCheckmarks(currentSelected);

    // animate
    void this.dropdownEl.offsetHeight;
    this.dropdownEl.classList.remove('ss-transition-none', 'invisible');
    requestAnimationFrame(() => {
      this.dropdownEl.classList.remove('opacity-0', 'scale-95');
      this.dropdownEl.classList.add('opacity-100', 'scale-100');
    });

    this._setupOutside();
    this._attachObservers();
    this.isDropdownOpen = true;
  }

  close() {
    if (!this.isDropdownOpen) return;

    this.dropdownEl.classList.add('opacity-0', 'scale-95');
    this.dropdownEl.classList.remove('opacity-100', 'scale-100');

    const onEnd = () => {
      this.dropdownEl.classList.add('hidden');
      this.dropdownEl.removeEventListener('transitionend', onEnd);
    };
    this.dropdownEl.addEventListener('transitionend', onEnd);

    this.buttonTarget.setAttribute('aria-expanded', 'false');

    this._unbindDropdownHandlers();
    this._teardownOutside();
    this._detachObservers();
    this.isDropdownOpen = false;
  }

  filterOptions(event) {
    if (event?.target) {
      const q = this._normalize(event.target.value || '');
      this._toggleClearBtn(q.length > 0);
      this._filter(q);
      this._positionDropdown();
    }
  }

  _handleLiClick(li) {
    const value = (li?.dataset.value || '').trim();
    const label = (li?.dataset.label || li?.textContent || value).trim();
    this._applySelection(label, value);
  }

  _applySelection(label, value) {
    this._updateValue(label, value);
    this._updateCheckmarks(value);
    this.close();
  }

  _updateValue(label, rawValue) {
    const spanEl = this.buttonTarget.querySelector('span');
    if (spanEl) {
      spanEl.textContent = label || this.buttonTarget.dataset.placeholder || '';
    } else {
      this.buttonTarget.innerText = label || this.buttonTarget.dataset.placeholder || '';
    }

    const ph = this.buttonTarget.dataset.placeholder || '';
    const isPlaceholder = !rawValue || label === ph;
    this.buttonTarget.classList.toggle('text-black-400', !!isPlaceholder);
    this.buttonTarget.classList.toggle('text-black-1000', !isPlaceholder);

    if (this._hiddenField) {
      this._hiddenField.value = rawValue;
      this._hiddenField.dispatchEvent(new Event('change', { bubbles: true }))
    }
  }

  _getHiddenValue() {
    return this._hiddenField ? this._hiddenField.value : '';
  }

  _updateCheckmarks(selectedValue) {
    if (!this.dropdownEl) return;

    const value = String(selectedValue || '').trim();
    const items = this.dropdownEl.querySelectorAll('li[role="option"][data-value]');

    items.forEach((li) => {
      const key = (li?.dataset.value || '').trim();
      const isSelected = !!value && key === value;
      const check = li?.querySelector('[data-ss-check]');

      li?.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      li?.classList.toggle('hover:bg-black-50', !isSelected);
      if (check) check.classList.toggle('hidden', !isSelected);
    });
  }

  _bindDropdownHandlers() {
    if (this._dropdownHandlersBound || !this.dropdownEl) return;

    this._onDropdownClick = (e) => {
      const li = e?.target?.closest?.('li[data-value]');
      if (li && this.dropdownEl.contains(li)) {
        e.preventDefault();
        e.stopPropagation();
        this._handleLiClick(li);
        return;
      }
      const clearBtn = e?.target?.closest?.('[data-single-select-with-search-target="clearSearchBtn"]');
      if (clearBtn && this.dropdownEl.contains(clearBtn)) {
        e.preventDefault();
        e.stopPropagation();
        this._clearSearch();
        return;
      }
      const clearSelection = e?.target?.closest?.('[data-single-select-with-search-clear-selection]');
      if (clearSelection && this.dropdownEl.contains(clearSelection)) {
        e.preventDefault();
        e.stopPropagation();
        this._applySelection('', '');
      }
    };

    this.dropdownEl.addEventListener('click', this._onDropdownClick);
    this._grabCommonRefs();

    if (this._searchEl) {
      this._onSearchInput = () => {
        const q = this._normalize(this._searchEl.value || '');
        this._toggleClearBtn(q.length > 0);
        this._filter(q);
        this._positionDropdown();
      };
      this._onSearchKeydown = (e) => {
        if (!e) return;
        if (e.key === 'Escape') {
          if (this._searchEl.value) {
            this._clearSearch();
          } else {
            this.close();
          }
          e.preventDefault();
        } else if (e.key === 'Enter') {
          const visible = this._visibleOptions();
          if (visible.length === 1) {
            this._handleLiClick(visible[0]);
            e.preventDefault();
          }
        }
      };

      this._searchEl.addEventListener('input', this._onSearchInput);
      this._searchEl.addEventListener('keydown', this._onSearchKeydown);
    }

    if (this._clearBtnEl) {
      this._onClearClick = (e) => {
        e?.preventDefault();
        e?.stopPropagation();
        this._clearSearch();
      };
      this._clearBtnEl.addEventListener('click', this._onClearClick);
    }

    this._dropdownHandlersBound = true;
  }

  _clearSearch() {
    if (!this._searchEl) return;
    this._searchEl.value = '';
    this._toggleClearBtn(false);
    this._filter('');
    try {
      this._searchEl.focus({ preventScroll: true });
    } catch (_) {}
    this._positionDropdown();
  }

  _grabCommonRefs() {
    this._searchEl = this.dropdownEl.querySelector(
      '[data-single-select-with-search-target="search"]',
    );
    this._clearBtnEl = this.dropdownEl.querySelector(
      '[data-single-select-with-search-target="clearSearchBtn"]',
    );
    this._emptyEl = this.dropdownEl.querySelector('[data-ss-empty]');
  }

  _unbindDropdownHandlers() {
    if (!this._dropdownHandlersBound || !this.dropdownEl) return;

    this.dropdownEl.removeEventListener('click', this._onDropdownClick);

    if (this._searchEl) {
      this._searchEl.removeEventListener('input', this._onSearchInput);
      this._searchEl.removeEventListener('keydown', this._onSearchKeydown);
    }
    if (this._clearBtnEl) {
      this._clearBtnEl.removeEventListener('click', this._onClearClick);
    }

    this._onDropdownClick = null;
    this._onSearchInput = null;
    this._onSearchKeydown = null;
    this._onClearClick = null;

    this._dropdownHandlersBound = false;
  }

  _toggleClearBtn(show) {
    if (this._clearBtnEl) this._clearBtnEl.classList.toggle('hidden', !show);
  }

  _normalize(str) {
    return (str || '')
      .toString()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '');
  }

  _filter(q) {
    const list = this.dropdownEl;
    const items = list.querySelectorAll('li[role="option"][data-value]');
    const qq = this._normalize(q);
    let visible = 0;

    items.forEach((li) => {
      const raw = li.getAttribute('data-label') || li.textContent || '';
      const match = !qq || this._normalize(raw).includes(qq);
      li.hidden = !match;
      if (match) visible += 1;
    });

    list.querySelectorAll('[data-group-header]').forEach((header) => {
      let sibling = header.nextElementSibling;
      let hasVisibleOption = false;
      while (sibling && !sibling.matches('[data-group-header]')) {
        if (sibling.matches('li[role="option"][data-value]') && !sibling.hidden) {
          hasVisibleOption = true;
          break;
        }
        sibling = sibling.nextElementSibling;
      }
      header.hidden = !hasVisibleOption;
    });

    if (this._emptyEl) this._emptyEl.hidden = !(qq && visible === 0);
  }

  _visibleOptions() {
    const list = this.dropdownEl;
    const all = list.querySelectorAll('li[role="option"][data-value]');
    const out = [];
    for (let i = 0; i < all.length; i++) {
      const li = all[i];
      if (!li.hidden) out.push(li);
    }
    return out;
  }

  _ensurePortalRoot() {
    let root = document.getElementById('ui-layer-root');
    if (!root) {
      root = document.createElement('div');
      root.id = 'ui-layer-root';
      root.classList.add('ui-layer-root');
      root.setAttribute('data-turbo-permanent', '');
      document.body.appendChild(root);
    }
    return root;
  }

  _positionDropdown() {
    const btnRect = this.buttonTarget.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const M = 8;

    const minW = this.hasMinWidthValue ? this.minWidthValue : 200;
    const maxW = this.hasMaxWidthValue ? this.maxWidthValue : Infinity;
    const width = Math.max(minW, Math.min(btnRect.width, maxW));

    this.dropdownEl.classList.add('ss-no-max-h');
    const naturalH = this.dropdownEl.offsetHeight || 300;
    this.dropdownEl.classList.remove('ss-no-max-h');

    const spaceBelow = vh - btnRect.bottom - M;
    const spaceAbove = btnRect.top - M;
    const wantsTop = this.directionValue === 'top';
    const wantsBottom = this.directionValue === 'bottom';
    const openUp = wantsTop
      ? true
      : wantsBottom
        ? false
        : spaceBelow < Math.min(naturalH, 320) && spaceAbove > spaceBelow;

    let top = openUp ? Math.max(M, btnRect.top - Math.min(naturalH, spaceAbove)) : btnRect.bottom;
    let maxH = openUp ? spaceAbove : spaceBelow;
    maxH = Math.max(120, Math.min(naturalH, maxH));

    let left = btnRect.left;
    if (left + width + M > vw) left = Math.max(M, vw - width - M);
    if (left < M) left = M;

    this._setVar('--ss-left', `${Math.round(left)}px`);
    this._setVar('--ss-top', `${Math.round(top)}px`);
    this._setVar('--ss-width', `${Math.round(width)}px`);
    this._setVar('--ss-max-h', `${Math.floor(maxH)}px`);
  }

  _setVar(name, value) {
    this.dropdownEl.style.setProperty(name, value);
  }

  _setupOutside() {
    if (this.outsideBound) return;

    this._onDocPointerDown = (e) => {
      const t = e?.target;
      const insideDropdown = t && this.dropdownEl.contains(t);
      const insideComponent = t && this.element.contains(t);
      if (!insideDropdown && !insideComponent) this.close();
    };
    this._onDocKeydown = (e) => {
      if (e?.key === 'Escape') this.close();
    };

    document.addEventListener('pointerdown', this._onDocPointerDown, true);
    document.addEventListener('keydown', this._onDocKeydown);
    this.outsideBound = true;
  }

  _teardownOutside() {
    if (!this.outsideBound) return;
    document.removeEventListener('pointerdown', this._onDocPointerDown, true);
    document.removeEventListener('keydown', this._onDocKeydown);
    this.outsideBound = false;
  }

  _attachObservers() {
    const self = this;
    this._reposition = function () {
      if (self.isDropdownOpen) self._positionDropdown();
    };
    this._onScroll = function (e) {
      if (self.dropdownEl && self.dropdownEl.contains(e.target)) return;
      self._reposition();
    };
    window.addEventListener('scroll', this._onScroll, true);
    window.addEventListener('resize', this._reposition);

    this.ro = new ResizeObserver(this._reposition);
    this.ro.observe(this.buttonTarget);

    if (window.visualViewport) {
      this.vvo = { onResize: this._reposition, onScroll: this._reposition };
      window.visualViewport.addEventListener('resize', this.vvo.onResize);
      window.visualViewport.addEventListener('scroll', this.vvo.onScroll);
    }
  }

  _detachObservers() {
    window.removeEventListener('scroll', this._onScroll, true);
    window.removeEventListener('resize', this._reposition);
    this.ro?.disconnect();
    this.ro = undefined;
    if (this.vvo) {
      window.visualViewport.removeEventListener('resize', this.vvo.onResize);
      window.visualViewport.removeEventListener('scroll', this.vvo.onScroll);
      this.vvo = undefined;
    }
  }
}
